import { useEffect, useId, useRef } from "react";

import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { useHintHover } from "@/components/hints/useHintHover";
import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { useStore } from "@/store";
import { twMerge } from "@/utils/twMerge";
import { useRovingNav } from "@/utils/useRovingNav";

import { CollapsedPopover } from "../CollapsedPopover";

import { SymbolPaletteButton } from "./SymbolPaletteButton";
import { SymbolPaletteContent } from "./SymbolPaletteContent";
import { symbolPaletteKeys, type SymbolPaletteItem } from "./types";

import type { DocumentModel } from "@/models/DocumentModel";

export const symbolPaletteItems: SymbolPaletteItem[] = [
  {
    key: symbolPaletteKeys.GREEK_ALPHABET,
    nameIntlKey: "sidebar.actionsPalette.greekAlphabet",
    shortNameIntlKey: "sidebar.actionsPalette.shortGreekAlphabet",
    symbol: "ω",
  },
  // {
  //   nameIntlKey: "sidebar.actionsPalette.greekItalic",
  //   key: symbolPaletteKeys.GREEK_ITALIC,
  //   symbol: "𝛺",
  // },
  {
    key: symbolPaletteKeys.OPERATORS,
    nameIntlKey: "sidebar.actionsPalette.operators",
    symbol: "+",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.logic",
    key: symbolPaletteKeys.LOGIC,
    symbol: "∀",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.set",
    key: symbolPaletteKeys.SET,
    symbol: "∈",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.relationshipOperators",
    shortNameIntlKey: "sidebar.actionsPalette.shortRelationshipOperators",
    key: symbolPaletteKeys.RELATIONSHIP_OPERATORS,
    symbol: "=",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.doubleStroke",
    key: symbolPaletteKeys.DOUBLE_STROKE,
    symbol: "ℕ",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.calculus",
    key: symbolPaletteKeys.CALCULUS,
    symbol: "∫",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.arrows",
    key: symbolPaletteKeys.ARROWS,
    symbol: "→",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.serif",
    key: symbolPaletteKeys.SERIF,
    symbol: "𝚃",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.circled",
    key: symbolPaletteKeys.CIRCLED,
    symbol: "Ⓐ",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.italic",
    key: symbolPaletteKeys.ITALIC,
    symbol: "𝘐",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.script",
    key: symbolPaletteKeys.SCRIPT,
    symbol: "𝒜",
  },
  {
    nameIntlKey: "sidebar.actionsPalette.other",
    shortNameIntlKey: "sidebar.actionsPalette.shortOther",
    key: symbolPaletteKeys.OTHER,
  },
];

type Props = {
  doc: DocumentModel;
  defaultOpen?: boolean;
  isCollapsed?: boolean;
};

const Content = observer(({ doc, isCollapsed }: { doc: DocumentModel; isCollapsed?: boolean }) => {
  const { view } = useStore();
  const t = useTranslations();
  const activeSymbolPalette = symbolPaletteItems.find((i) => i.key === view.symbolPaletteActiveKey);
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const tabId = (key: string) => `${baseId}-tab-${key}`;

  // Manual activation: arrows only move focus, Enter/Space opens a category (or closes the open one)
  const tabListRef = useRef<HTMLDivElement>(null);
  useRovingNav(tabListRef, "horizontal", { tabStop: '[aria-selected="true"]', wrap: true });

  return (
    <div className={twMerge("p-1.25 pt-0", isCollapsed && "p-0")}>
      <div
        ref={tabListRef}
        role="tablist"
        aria-label={t("sidebar.actionsPalette.title")}
        className={twMerge("flex flex-row flex-wrap px-1.25 gap-1.5", isCollapsed && "p-0")}
      >
        {symbolPaletteItems.map((item) => (
          <SymbolPaletteButton key={item.key} item={item} id={tabId(item.key)} panelId={panelId} />
        ))}
      </div>
      {activeSymbolPalette && (
        <div className="-m-1.25 mt-0">
          <SymbolPaletteContent
            item={activeSymbolPalette}
            doc={doc}
            id={panelId}
            tabId={tabId(activeSymbolPalette.key)}
          />
        </div>
      )}
    </div>
  );
});

export const SymbolPalette = observer(function SymbolPalette({
  doc,
  defaultOpen,
  isCollapsed,
}: Props) {
  const { view } = useStore();
  const t = useTranslations();
  const hintHandlers = useHintHover("symbolsPalette");
  const isOpen = view.isSymbolPaletteVisible;

  useEffect(() => {
    if (defaultOpen) {
      runInAction(() => {
        view.isSymbolPaletteVisible = true;
        view.symbolPaletteActiveKey = symbolPaletteItems[0]?.key;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultOpen]);

  if (isCollapsed) {
    return (
      <CollapsedPopover
        icon={<Icon name="Symbols" />}
        tooltipTitle={t("sidebar.actionsPalette.title")}
        title={t("sidebar.actionsPalette.title")}
      >
        <Content doc={doc} isCollapsed />
      </CollapsedPopover>
    );
  }

  return (
    <Sidebar.GroupSection data-panel="symbol_palette" className="p-0">
      <button
        onClick={view.toggleSymbolPalette}
        className="flex w-full"
        type="button"
        aria-expanded={isOpen}
        {...hintHandlers}
      >
        <Sidebar.SectionTitle className="flex">
          <Icon name="ChevronDown" className={twMerge(!isOpen && "-rotate-90")} />
          <span className="tracking-tight">{t("sidebar.actionsPalette.title")}</span>
          {isOpen && (
            <span className="flex flex-1 h-[1px] mx-1.25 my-auto bg-gray-300 dark:bg-gray-600" />
          )}
        </Sidebar.SectionTitle>
      </button>
      {view.isSymbolPaletteVisible && <Content doc={doc} />}
    </Sidebar.GroupSection>
  );
});
